// src/components/institution/InstitutionManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  X, 
  MessageCircle,
  Search, 
  Filter, 
  Upload, 
  Download, 
  Grid, 
  List,
  School,
  Users,
  Calendar,
  Bell,
  FileText,
  Image,
  User,
  Building2,
  Award,
  Settings,
  Edit,
  Trash2,
  Eye,
  ChevronDown,
  Save
} from 'lucide-react';
import { institutionService } from '../../services/institutionService';

// Tab Components
import { AcademicProgramsTab } from './manager/AcademicProgramsTab';
import { StaffLeadershipTab } from './manager/StaffLeadershipTab';
import { EventsCalendarTab } from './manager/EventsCalendarTab';
import { PostsSectionForm } from '../manager/shared/PostsSectionForm';
import { ResourcesTab } from './manager/ResourcesTab';
import { FacilitiesTab } from './manager/FacilitiesTab';
import { AdmissionsTab } from './manager/AdmissionsTab';
import { GalleriesTab } from './manager/GalleriesTab';
import { AnalyticsTab } from './manager/AnalyticsTab';
import { HeroSectionTab } from './manager/HeroSectionTab';
import { AboutSectionTab } from './manager/AboutSectionTab';


const MANAGER_TABS = [
  { 
    id: 'hero', 
    label: 'Hero Section', 
    icon: School,
    description: 'Configure homepage hero section with logo, title, and stats'
  },
  { 
    id: 'about', 
    label: 'About Section', 
    icon: FileText,
    description: 'Manage institution information, mission, vision, and values'
  },
  { 
    id: 'programs', 
    label: 'Academic Programs', 
    icon: School,
    description: 'Manage courses, departments, and academic offerings'
  },
  { 
    id: 'staff', 
    label: 'Staff & Leadership', 
    icon: Users,
    description: 'Manage staff profiles, leadership team, and committees'
  },
  { 
    id: 'events', 
    label: 'Events & Calendar', 
    icon: Calendar,
    description: 'Manage institutional events and academic calendar'
  },
  { 
    id: 'posts', 
    label: 'Posts & Announcements', 
    icon: MessageCircle, 
    description: 'Manage community posts, news, and announcements'
  },
  { 
    id: 'resources', 
    label: 'Resources', 
    icon: FileText,
    description: 'Manage documents, forms, and educational resources'
  },
  { 
    id: 'facilities', 
    label: 'Facilities', 
    icon: Building2,
    description: 'Manage campus facilities and infrastructure'
  },
  { 
    id: 'admissions', 
    label: 'Admissions', 
    icon: User,
    description: 'Manage admission processes and requirements'
  },
  { 
    id: 'galleries', 
    label: 'Photo Galleries', 
    icon: Image,
    description: 'Manage photo galleries for events and activities'
  },
  { 
    id: 'analytics', 
    label: 'Analytics', 
    icon: Award,
    description: 'View performance metrics and engagement analytics'
  }

];

export const InstitutionManager = ({ bookId, book, user }) => {
  const [activeTab, setActiveTab] = useState('programs');
  const [loading, setLoading] = useState(true);
  const [institutionData, setInstitutionData] = useState(null);
  const [stats, setStats] = useState({
    programs: 0,
    staff: 0,
    events: 0,
    news: 0,
    resources: 0,
    facilities: 0,
    galleries: 0,
    totalViews: 0
  });

  useEffect(() => {
    loadInstitutionData();
    loadStats();
  }, [bookId]);

  const loadInstitutionData = async () => {
    try {
      const data = await institutionService.getInstitutionData(bookId);
      setInstitutionData(data);
    } catch (error) {
      console.error('Error loading institution data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await institutionService.getInstitutionStats(bookId);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleDataUpdate = async () => {
    await loadInstitutionData();
    await loadStats();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-4">
              {institutionData?.logoUrl && (
                <img 
                  src={institutionData.logoUrl} 
                  alt="Institution Logo" 
                  className="h-20 w-20 rounded-full object-cover ring-2 ring-white/20"
                />
              )}
            <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              {institutionData?.botName} Manager
            </h1>
           
            <p className="text-gray-600 mt-1">
              Manage your institution's content, staff, and resources
            </p>
             </div>
          </div>
          </div>
          {/* Quick Stats */}
          <div className="hidden md:flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">{stats.programs}</div>
              <div className="text-xs text-gray-500">Programs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.staff}</div>
              <div className="text-xs text-gray-500">Staff</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.events}</div>
              <div className="text-xs text-gray-500">Events</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.totalViews}</div>
              <div className="text-xs text-gray-500">Total Views</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <div className="space-y-2">
              {MANAGER_TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const count = stats[tab.id] || 0;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all ${
                      isActive
                        ? 'bg-indigo-50 border-2 border-indigo-200 text-indigo-700'
                        : 'hover:bg-gray-50 border-2 border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${
                      isActive ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">{tab.label}</div>
                        {count > 0 && (
                          <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                            isActive 
                              ? 'bg-indigo-200 text-indigo-800' 
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {count}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 leading-tight">
                        {tab.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Institution Overview Card */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                {book?.logo && (
                  <img 
                    src={book.logo} 
                    alt="Institution Logo" 
                    className="h-10 w-10 rounded-lg object-cover"
                  />
                )}
                <div>
                  <h3 className="font-medium text-gray-900 text-sm">
                    {book?.botName || 'Institution Name'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {book?.institutionSettings?.institutionType || 'Educational Institution'}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-indigo-50 p-2 rounded">
                  <div className="font-medium text-indigo-900">Content</div>
                  <div className="text-indigo-600">
                    {stats.programs + stats.events + stats.news + stats.resources} items
                  </div>
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium text-green-900">Team</div>
                  <div className="text-green-600">{stats.staff} members</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Render Active Tab Content */}

              {activeTab === 'hero' && (
              <HeroSectionTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'about' && (
              <AboutSectionTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}
            {activeTab === 'programs' && (
              <AcademicProgramsTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'staff' && (
              <StaffLeadershipTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'events' && (
              <EventsCalendarTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}
            {activeTab === 'posts' && (
              <PostsSectionForm
                user={user}
                bookId={bookId}
                templateType="institution"
                data={{ posts: [] }}
                onChange={handleDataUpdate}
                errors={{}}
              />
            )}

            {activeTab === 'resources' && (
              <ResourcesTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'facilities' && (
              <FacilitiesTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'admissions' && (
              <AdmissionsTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'galleries' && (
              <GalleriesTab
                bookId={bookId}
                institutionData={institutionData}
                onUpdate={handleDataUpdate}
              />
            )}

            {activeTab === 'analytics' && (
              <AnalyticsTab
                bookId={bookId}
                institutionData={institutionData}
                stats={stats}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};